/**
 * Recognising a video link in post text, and turning it into a player source.
 *
 * ── Why one module ──────────────────────────────────────────────────────────
 * These three functions existed twice, byte-identical, in MembershipShell and
 * CommunityFeed — and a third, narrower variant lived in the community views.
 * That divergence is what produced a run of bugs in one sitting: a YouTube
 * Short embedded in a lesson and rendered a blank box in the feed; a link
 * pasted on create became a player while the same link pasted on edit stayed
 * blue text. One copy cannot disagree with itself.
 *
 * (`toEmbed` in ./toEmbed.js is the sibling used by lessons and rewards, where
 * a URL is stored on its own rather than found inside free text.)
 */

/** Normalise a video URL into something embeddable, or report it as a link. */
export function videoEmbedUrl(url) {
  if (!url) return null;

  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return { type: "iframe", src: `https://www.youtube.com/embed/${m[1]}` };

  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return { type: "iframe", src: `https://player.vimeo.com/video/${m[1]}` };

  m = url.match(/(?:wistia\.com\/medias\/|wistia\.net\/(?:embed\/iframe|medias)\/|wi\.st\/medias\/)([\w-]+)/);
  if (m) return { type: "iframe", src: `https://fast.wistia.net/embed/iframe/${m[1]}` };

  if (/\.(mp4|webm|ogg|ogv|mov|m4v)(\?|$)/i.test(url)) return { type: "video", src: url };

  return { type: "link", src: url };
}

/** Trailing punctuation belongs to the sentence, not the URL. */
const clean = (u) => u.replace(/[)\].,]+$/, "");

/** The first EMBEDDABLE video link in free text, or null. */
export function extractVideoUrl(text) {
  if (!text) return null;
  for (const u of text.match(/https?:\/\/[^\s]+/gi) || []) {
    const v = videoEmbedUrl(clean(u));
    if (v && (v.type === "iframe" || v.type === "video")) return clean(u);
  }
  return null;
}

/** The first NON-video link in free text — the one that gets a preview card. */
export function extractLinkUrl(text) {
  if (!text) return null;
  for (const u of text.match(/https?:\/\/[^\s]+/gi) || []) {
    const v = videoEmbedUrl(clean(u));
    if (v && v.type === "link") return clean(u);
  }
  return null;
}
